import SortOrderEnum from "../../../models/SortOrderEnum";
import * as _ from "lodash-es";
import OrderFilter from "./OrderFilter/OrderFilter";
import OrderSourceEnum from "@models/OrderSourceEnum";
import IOrder from "@models/IOrder";
import IUser from "@models/IUser";
import ISupplier from "@models/ISupplier";
import IUserUI from "@models/IUserUI";
import AccessTypesEnum from "@models/AccessTypesEnum";
import OrderEntryService from "@services/OrderEntryService";
import IReadOnlyOrderStateDetail from "@models/IReadOnlyOrderStateDetail";
import UIService from "@services/UIService";
import UserTypesEnum from "@models/UserTypesEnum";

const getQueryString = (sortCol: string, sortOrder: SortOrderEnum, includeQueryString?: string, queryStringArr?: string[]): string => {
    let query: string[] = [];
    let queryString = "";

    if (!_.isNil(sortCol)) {
        if (sortCol === "priority") {
            sortCol = "Vendor.priority";
        }

        if (sortCol === "Vendor") {
            sortCol = "Vendor.name";
        }

        if (sortCol === "order") {
            sortCol = "name";
        }

        query.push(`order_by=${sortCol}`);
    }

    if (!_.isNil(sortOrder)) {
        query.push(`order_dir=${sortOrder}`);
    }

    if (query.length) {
        if (includeQueryString) {
            queryString = includeQueryString + "&";
        } else {
            queryString = "?";

            if (queryStringArr) {
                query = [...query, ...queryStringArr];
            }
        }

        queryString += query.join("&");
    } else {
        if (includeQueryString) {
            return includeQueryString;
        }
    }

    return queryString;
};

const getFilterQuery = (state: typeof OrderFilter.prototype.state, isQuotes: boolean): string[] => {
    const query: string[] = [];

    if (!_.isNil(state.selectedUser)) {
        query.push(`user=${state.selectedUser.guid}`);
    }

    if (!_.isNil(state.selectedVendor)) {
        query.push(`vendor=${state.selectedVendor.guid}`);
        query.push(`vendor_name=${encodeURIComponent(state.selectedVendor.name)}`);
    }

    if (!_.isNil(state.selectedClient)) {
        query.push(`client=${state.selectedClient.guid}`);
    }

    if (!_.isNil(state.order) && state.order.length) {
        query.push(`order=${encodeURIComponent(state.order)}`);
    }

    if (!_.isNil(state.parent_order) && state.parent_order.length) {
        query.push(`parent_order=${encodeURIComponent(state.parent_order)}`);
    }

    if (!_.isNil(state.po_no) && state.po_no.length) {
        query.push(`po_no=${encodeURIComponent(state.po_no)}`);
    }

    if (!_.isNil(state.description) && state.description.length) {
        query.push(`description=${encodeURIComponent(state.description)}`);
    }

    if (!_.isNil(state.is_dropship) && state.is_dropship) {
        query.push(`is_dropship=${state.is_dropship ? "1" : "0"}`);
    }

    if (!_.isNil(state.is_local_pickup) && state.is_local_pickup) {
        query.push(`is_local_pickup=${state.is_local_pickup ? "1" : "0"}`);
    }

    if (!_.isNil(state.is_local_delivery) && state.is_local_delivery) {
        query.push(`is_local_delivery=${state.is_local_delivery ? "1" : "0"}`);
    }

    if (!_.isNil(state.has_serial_items) && state.has_serial_items.toString().length) {
        query.push(`has_serial_items=${state.has_serial_items ? "1" : "0"}`);
    }

    if (!_.isNil(state.category_id) && state.category_id.length) {
        query.push(`category_id=${state.category_id}`);
    }

    if (!_.isNil(state.selected_order_categories) && state.selected_order_categories.length) {
        query.push(`selected_order_categories=${state.selected_order_categories}`);
    }

    if (!_.isNil(state.workflow_id) && state.workflow_id.length) {
        query.push(`workflow_id=${state.workflow_id}`);
    }

    if (!_.isNil(state.dateSelectedValue)) {
        if (state.dateSelectedValue.toString() !== "Invalid date") {
            query.push(`updated_at=${state.dateSelectedValue.format("YYYY-MM-DD")}`);
        }
    }

    if (!_.isNil(state.startDateSelectedValue) && !_.isNil(state.endDateSelectedValue)) {
        query.push(`ship_date_range_start=${state.startDateSelectedValue.format("YYYY-MM-DD")}`);
        query.push(`ship_date_range_end=${state.endDateSelectedValue.format("YYYY-MM-DD")}`);
    }

    if (!_.isNil(state.doNotShipBeforeStartDateSelectedValue) && !_.isNil(state.doNotShipBeforeEndDateSelectedValue)) {
        query.push(`do_not_ship_before_start_date=${state.doNotShipBeforeStartDateSelectedValue.format("YYYY-MM-DD")}`);
        query.push(`do_not_ship_before_end_date=${state.doNotShipBeforeEndDateSelectedValue.format("YYYY-MM-DD")}`);
    }

    if (!_.isNil(state.createdAtStartDateSelectedValue) && !_.isNil(state.createdAtEndDateSelectedValue)) {
        query.push(`created_at_range_start=${state.createdAtStartDateSelectedValue.format("YYYY-MM-DD")}`);
        query.push(`created_at_range_end=${state.createdAtEndDateSelectedValue.format("YYYY-MM-DD")}`);
    }

    if (!_.isNil(state.order_state_id) && state.order_state_id) {
        query.push(`order_state=${state.order_state_id}`);
    }

    if (!_.isNil(state.lmc_product_type_id) && state.lmc_product_type_id) {
        query.push(`lmc_product_type_id=${state.lmc_product_type_id}`);
    }

    if (!_.isNil(state.lmc_offering_category_id) && state.lmc_offering_category_id) {
        query.push(`lmc_offering_category_id=${state.lmc_offering_category_id}`);
    }

    if (!_.isNil(state.vendor_source_id) && state.vendor_source_id) {
        query.push(`vendor_source_id=${state.vendor_source_id}`);
    }

    if (!_.isNil(state.route_id) && state.route_id) {
        query.push(`route_id=${state.route_id}`);
    }

    if (!_.isNil(state.vendor_source_location_id) && state.vendor_source_location_id) {
        query.push(`vendor_source_location_id=${state.vendor_source_location_id}`);
    }

    if (!_.isNil(state.include_sub_accounts) && state.include_sub_accounts) {
        query.push(`include_sub_accounts=${state.include_sub_accounts}`);
    }

    if (!_.isNil(state.deceasedStartDateSelectedValue) && !_.isNil(state.deceasedEndDateSelectedValue)) {
        query.push(`deceased_start_date=${state.deceasedStartDateSelectedValue.format("YYYY-MM-DD")}`);
        query.push(`deceased_end_date=${state.deceasedEndDateSelectedValue.format("YYYY-MM-DD")}`);
    }

    if (!isQuotes) {
        if (!_.isNil(state.catalogGuid) && state.catalogGuid.length) {
            query.push(`catalog=${state.catalogGuid}`);
        }

        if (!_.isNil(state.filter_older_three_months) && state.filter_older_three_months) {
            query.push(`filter_older_three_months=${state.filter_older_three_months}`);
        }
    }

    if (!_.isNil(state.tagsToFind) && state.tagsToFind.length) {
        query.push(`tags=${state.tagsToFind.join(",")}`);
    }

    // Quotes are always preliminary
    if (!isQuotes) {
        if (!_.isNil(state.filter_preliminary) && state.filter_preliminary) {
            query.push(`filter_preliminary=${state.filter_preliminary}`);
        }

        if (!_.isNil(state.filter_cancelled) && state.filter_cancelled) {
            query.push(`filter_cancelled=${state.filter_cancelled}`);
        }

        if (!_.isNil(state.filter_shipped) && state.filter_shipped) {
            query.push(`filter_shipped=${state.filter_shipped}`);
        }

        if (!_.isNil(state.filter_delivered) && state.filter_delivered) {
            query.push(`filter_delivered=${state.filter_delivered}`);
        }

        if (!_.isNil(state.filter_picked_up) && state.filter_picked_up) {
            query.push(`filter_picked_up=${state.filter_picked_up}`);
        }

        if (!_.isNil(state.filter_remakes) && state.filter_remakes) {
            query.push(`filter_remakes=${state.filter_remakes}`);
        }

        if (!_.isNil(state.deceased_last_name) && state.deceased_last_name.length) {
            query.push(`deceased_last_name=${encodeURIComponent(state.deceased_last_name)}`);
        }

        if (!_.isNil(state.filter_expedited) && state.filter_expedited) {
            query.push(`filter_expedited=${state.filter_expedited}`);
        }

        if (!_.isNil(state.filter_notes) && state.filter_notes) {
            query.push(`filter_notes=${state.filter_notes}`);
        }

        if (!_.isNil(state.filter_only_backorder) && state.filter_only_backorder) {
            query.push(`filter_only_backorder=${state.filter_only_backorder}`);
        }

        if (!_.isNil(state.filter_only_consignment) && state.filter_only_consignment) {
            query.push(`filter_only_consignment=${state.filter_only_consignment}`);
        }

        if (!_.isNil(state.third_party_order_ref) && state.third_party_order_ref.length) {
            query.push(`third_party_order_ref=${encodeURIComponent(state.third_party_order_ref)}`);
        }

        if (!_.isNil(state.fishbowl_so_id) && state.fishbowl_so_id.length) {
            query.push(`fishbowl_so_id=${encodeURIComponent(state.fishbowl_so_id)}`);
        }

        if (!_.isNil(state.has_accounting) && _.isBoolean(state.has_accounting)) {
            query.push(`has_accounting=${state.has_accounting}`);
        }

        if (!_.isNil(state.qbo_invoice_created) && _.isBoolean(state.qbo_invoice_created)) {
            query.push(`qbo_invoice_created=${state.qbo_invoice_created}`);
        }

        if (!_.isNil(state.qbo_invoice_emailed) && _.isBoolean(state.qbo_invoice_emailed)) {
            query.push(`qbo_invoice_emailed=${state.qbo_invoice_emailed}`);
        }

        if (!_.isNil(state.qbd_invoice_created) && _.isBoolean(state.qbd_invoice_created)) {
            query.push(`qbd_invoice_created=${state.qbd_invoice_created}`);
        }

        if (!_.isNil(state.qbd_sales_order_created) && _.isBoolean(state.qbd_sales_order_created)) {
            query.push(`qbd_sales_order_created=${state.qbd_sales_order_created}`);
        }

        if (!_.isNil(state.filter_order_type)) {
            query.push(`filter_order_type=${state.filter_order_type}`);
        }
    } else {
        // Quotes only filters
        if (!_.isNil(state.filter_closed) && _.isBoolean(state.filter_closed)) {
            query.push(`filter_closed=${state.filter_closed}`);
        }
    }

    return query;
};

const getReadOnlyStateDetail = (supplierModel: ISupplier, order: IOrder): IReadOnlyOrderStateDetail => {
    const orderSteps = OrderEntryService.getOrderSteps(supplierModel, order.order_state_id!, order.Workflow?.detail, order);
    const readOnlyOrderStateDetail: IReadOnlyOrderStateDetail = {};

    for (let i = 0; i < orderSteps.length; ++i) {
        const orderStep = orderSteps[i];

        if (i > 0) {
            const previousOrderStep = orderSteps[i - 1];
            const previousReadOnlyOrderStateDetail = readOnlyOrderStateDetail[previousOrderStep.order_state_id];

            readOnlyOrderStateDetail[orderStep.order_state_id] = {
                isVendorReadOnly: orderStep.isVendorReadOnly
                    ? orderStep.isVendorReadOnly
                    : previousReadOnlyOrderStateDetail.isVendorReadOnly || false,
                isSupplierReadOnly: orderStep.isSupplierReadOnly
                    ? orderStep.isSupplierReadOnly
                    : previousReadOnlyOrderStateDetail.isSupplierReadOnly || false,
                isSupplierAdminReadOnly: orderStep.isSupplierAdminReadOnly
                    ? orderStep.isSupplierAdminReadOnly
                    : previousReadOnlyOrderStateDetail.isSupplierAdminReadOnly || false,
                name: UIService.getOrderStateNameFromId(orderStep.order_state_id),
            };
        } else {
            readOnlyOrderStateDetail[orderStep.order_state_id] = {
                isVendorReadOnly: false,
                isSupplierReadOnly: false,
                isSupplierAdminReadOnly: false,
                name: UIService.getOrderStateNameFromId(orderStep.order_state_id),
            };
        }
    }

    return readOnlyOrderStateDetail;
};

const isReadOnly = (options: {
    order: IOrder;
    isOfflineOrder: boolean;
    hasEditMode: boolean;
    supplierModel: ISupplier;
    userModel: IUserUI;
    section?: string;
}) => {
    if (options.order && !options.isOfflineOrder) {
        switch (options.order.order_source_id) {
            // case OrderSourceEnum.ORDERWERKS:
            case OrderSourceEnum.OTHER:
            case OrderSourceEnum.APP:
                return false;

            case OrderSourceEnum.SHOPIFY:
            case OrderSourceEnum.AMAZON:
            case OrderSourceEnum.OPENCART:
            case OrderSourceEnum["3DCART"]:
            case OrderSourceEnum.BIG_CARTEL:
            case OrderSourceEnum.BIGCOMMERCE:
            case OrderSourceEnum.EBAY:
            case OrderSourceEnum.ETSY:
            case OrderSourceEnum.MAGENTO:
            case OrderSourceEnum.PRESTASHOP:
            case OrderSourceEnum.SALESFORCE_B2B:
            case OrderSourceEnum.SALESFORCE_B2C:
            case OrderSourceEnum.SHOPIFY_PLUS:
            case OrderSourceEnum.SQUARESPACE:
            case OrderSourceEnum.VOLUSION:
            case OrderSourceEnum.WEEBLY:
            case OrderSourceEnum.WIX:
            case OrderSourceEnum.WOOCOMMERCE:
            case OrderSourceEnum.WAYFAIR:
                return true;
        }

        if (!options.hasEditMode) {
            return true;
        }

        if (options.order.is_cancelled) {
            return true;
        }

        // If not a supplier and we want to disable vendor quote order entry for quotes
        if (
            options.supplierModel.disable_vendor_quote_order_entry &&
            options.order?.is_quote &&
            options.userModel &&
            !options.userModel?.isSupplierUser
        ) {
            return true;
        } else {
            if (options.userModel.supplierUserToken?.Access_Type?.id === AccessTypesEnum.READ_ONLY) {
                return true;
            }

            if (options.supplierModel) {
                let isReadOnly = false;

                // new way
                const readOnlyStateDetail = getReadOnlyStateDetail(options.supplierModel, options.order);

                const orderStateReadOnlyDetail = readOnlyStateDetail[options.order!.order_state_id!];

                if (orderStateReadOnlyDetail) {
                    if (options.userModel?.isSupplierUser) {
                        if (options.userModel.userType!.id === UserTypesEnum.ADMIN) {
                            isReadOnly = orderStateReadOnlyDetail.isSupplierAdminReadOnly;
                        } else {
                            isReadOnly = orderStateReadOnlyDetail.isSupplierReadOnly;
                        }
                    } else {
                        isReadOnly = orderStateReadOnlyDetail.isVendorReadOnly;

                        if (options.supplierModel.disable_vendor_order_entry && !options.order!.is_quote && !isReadOnly) {
                            if (options.section === "DETAILS_PANEL") {
                                isReadOnly = false;
                            } else {
                                isReadOnly = true;
                            }
                        }
                    }
                } else {
                    isReadOnly = true;
                }

                return isReadOnly;
            } else {
                return false;
            }
        }
    } else {
        return false;
    }
};

export const OrdersService = {
    getQueryString,
    getFilterQuery,
    isReadOnly,
};
